import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import Cookies from "universal-cookie";
import { getPythonApiAsyn } from "../../Services/PostAPI";
import "./Dashboard.css";
/* eslint-disable no-unused-vars */

const cookies = new Cookies();
const MONTH_OPTIONS = [
  { label: "This Month", value: "this_month" },
  { label: "1M", value: 1 },
  { label: "3M", value: 3 },
  { label: "6M", value: 6 },
];
const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316"];
const fmt = (n) => Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString();
const round2 = (n) => Math.round((n || 0) * 100) / 100;

function DashboardComponent() {
  const [period, setPeriod] = useState("this_month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const loggedInUserInfo = useSelector((state) => state?.userProfileReducer?.loggedInUserInfo);
  const userName = loggedInUserInfo?.first || loggedInUserInfo?.name || loggedInUserInfo?.username || "";

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let qs;
      if (period === "custom" && dateFrom && dateTo) {
        qs = `dashboard/card-stats?months=1&date_from=${dateFrom}&date_to=${dateTo}`;
      } else if (period === "this_month") {
        qs = `dashboard/card-stats?months=1&period=this_month`;
      } else {
        qs = `dashboard/card-stats?months=${period}`;
      }
      const result = await getPythonApiAsyn(qs, cookies.get("authorization"));
      if (result?.detail || result?.error) throw new Error(result?.detail || result?.error);
      setData(result);
    } catch (e) {
      setError(e.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo]);

  useEffect(() => { if (period !== "custom") fetchData(); }, [period]); // auto-fetch for preset periods
  // custom range is fetched only on Apply click

  const months = period === "this_month" ? 1 : period;
  const providers = data?.by_provider || [];
  const totals = data?.totals || {};

  const barOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "52%", distributed: true } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: providers.map((p) => p.provider),
      labels: { style: { fontSize: "12px", fontWeight: 600, colors: "#6b7280" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { formatter: (v) => fmtNum(v), style: { fontSize: "11px", colors: "#9ca3af" } } },
    colors: COLORS,
    legend: { show: false },
    grid: { borderColor: "#f3f4f6", strokeDashArray: 4, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
    tooltip: { y: { formatter: (v, { seriesIndex }) => seriesIndex === 0 ? fmtNum(v) + " cards" : "$" + fmt(v) } },
    fill: { type: "gradient", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.1, opacityFrom: 1, opacityTo: 0.85 } },
  };

  const kpiCards = [
    { label: "Fuel Cards Used", value: fmtNum(totals.cards_used), sub: "Unique cards transacted", accent: "#f97316", bg: "#fff7ed" },
  ];

  return (
    <div className="db-root">
      {/* ── Header ── */}
      <div className="db-hero">
        <div>
          <h1 className="db-hero-title">Welcome{userName ? `, ${userName}` : ""}</h1>
          <p className="db-hero-sub">
            {now.toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            {" · "}
            {now.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        <div className="db-period-pills">
          {MONTH_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`db-pill${period === opt.value ? " db-pill--active" : ""}`}
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          ))}
          <button
            className={`db-pill${period === "custom" ? " db-pill--active" : ""}`}
            onClick={() => setPeriod("custom")}
          >
            Custom
          </button>
          {period === "custom" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "4px 10px", fontSize: 13, color: "#374151", background: "#fff", outline: "none" }}
              />
              <span style={{ color: "#9ca3af", fontSize: 13 }}>→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "4px 10px", fontSize: 13, color: "#374151", background: "#fff", outline: "none" }}
              />
              <button
                className="db-pill db-pill--active"
                onClick={() => { if (dateFrom && dateTo) fetchData(); }}
                disabled={!dateFrom || !dateTo}
                style={{ opacity: (!dateFrom || !dateTo) ? 0.5 : 1 }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="db-skeleton-wrap">
          {/* KPI skeleton */}
          <div className="db-kpi-grid">
            {[0,1,2].map(i => (
              <div key={i} className="db-kpi-card">
                <div className="db-skel db-skel--icon" />
                <div style={{ flex: 1 }}>
                  <div className="db-skel db-skel--label" />
                  <div className="db-skel db-skel--value" />
                  <div className="db-skel db-skel--sub" />
                </div>
              </div>
            ))}
          </div>
          {/* Chart skeletons */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginBottom: 22 }}>
            {[0,1,2].map(i => (
              <div key={i} className="db-card">
                <div className="db-skel db-skel--title" />
                <div className="db-skel db-skel--chart" />
              </div>
            ))}
          </div>
          {/* Provider rows skeleton */}
          <div className="db-skel db-skel--title" style={{ marginBottom: 10 }} />
          {[0,1,2].map(i => (
            <div key={i} className="db-provider-card" style={{ marginBottom: 10 }}>
              <div className="db-skel db-skel--badge" />
              <div style={{ flex: 1 }}>
                <div className="db-skel db-skel--label" style={{ marginBottom: 8 }} />
                <div className="db-skel" style={{ height: 5, borderRadius: 99 }} />
              </div>
              <div style={{ display: "flex", gap: 20 }}>
                {[0,1,2].map(j => <div key={j} className="db-skel db-skel--stat" />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className="db-error-banner">{error}</div>}

      {!loading && !error && data && (
        <>
          {/* ── KPI cards ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 18, marginBottom: 24 }}>
            {/* Cards used */}
            {kpiCards.map((k) => (
              <div key={k.label} className="db-kpi-card">
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div className="db-kpi-icon-wrap" style={{ background: k.bg, color: k.accent, marginBottom: 0 }}>
                    {/* credit-card icon */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={k.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  </div>
                  <p className="db-kpi-label" style={{ margin: 0 }}>{k.label}</p>
                </div>
                <div style={{ background: "#fff7ed", borderRadius: 10, padding: "12px 14px" }}>
                  <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Cards</p>
                  <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>{k.value}</p>
                  <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{k.sub}</p>
                </div>
                <div className="db-kpi-bar" style={{ background: k.accent + "22" }}>
                  <div className="db-kpi-bar-fill" style={{ background: k.accent, width: "100%" }} />
                </div>
              </div>
            ))}

            {/* CAD + USD currency sub-cards */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              {[
                { label: "CAD Total Volume / Total Cost", flag: "🇨🇦", qty: totals?.CAD?.qty, qtyUnit: "Litres", amt: totals?.CAD?.amt, cur: "CA$", accent: "#10b981", bg: "#ecfdf5" },
                { label: "USD Total Volume / Total Cost", flag: "🇺🇸", qty: totals?.USD?.qty, qtyUnit: "gal", amt: totals?.USD?.amt, cur: "US$", accent: "#6366f1", bg: "#eef2ff" },
              ].map((c) => (
                <div key={c.label} className="db-kpi-card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="db-kpi-icon-wrap" style={{ background: c.bg, color: c.accent, marginBottom: 0 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16"/><path d="M3 22h10"/><path d="M13 8h2a2 2 0 0 1 2 2v3a1 1 0 0 0 1 1h0a1 1 0 0 0 1-1V9l-3-3"/><rect x="5" y="8" width="6" height="4" rx="1"/></svg>
                    </div>
                    <p className="db-kpi-label" style={{ margin: 0 }}>{c.label}</p>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ background: c.bg, borderRadius: 10, padding: "12px 14px" }}>
                      <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: c.accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.flag} Volume</p>
                      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>{fmt(c.qty)} <span style={{ fontSize: 13, fontWeight: 500, color: "#6b7280" }}>{c.qtyUnit}</span></p>
                    </div>
                    <div style={{ background: c.bg, borderRadius: 10, padding: "12px 14px" }}>
                      <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: c.accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.flag} Cost</p>
                      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>{c.cur}{fmt(c.amt)}</p>
                    </div>
                  </div>
                  <div className="db-kpi-bar" style={{ background: c.accent + "22" }}>
                    <div className="db-kpi-bar-fill" style={{ background: c.accent, width: "100%" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {providers.length === 0 && (
            <div className="db-empty">
              <p>No transaction data found for this period.</p>
            </div>
          )}

          {providers.length > 0 && (
            <>
              {/* ── 3-chart row: pie + fuel volume bar + cost bar ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginBottom: 22 }}>
                <div className="db-card">
                  <p className="db-card-title">Fuel Cards Used by Provider</p>
                  <ReactApexChart
                    options={{
                      chart: { type: "pie", toolbar: { show: false } },
                      labels: providers.map((p) => p.provider),
                      colors: COLORS,
                      legend: { position: "bottom", fontSize: "11px" },
                      dataLabels: { formatter: (val, { seriesIndex, w }) => w.globals.series[seriesIndex].toLocaleString() },
                      tooltip: { y: { formatter: (v) => fmtNum(v) + " cards" } },
                      stroke: { show: false },
                    }}
                    series={providers.map((p) => p.cards_used)}
                    type="pie"
                    height={280}
                  />
                </div>

                <div className="db-card">
                  <p className="db-card-title">Volume by Provider — CAD (L) · USD (gal)</p>
                  <ReactApexChart
                    options={{
                      ...barOptions,
                      plotOptions: { bar: { borderRadius: 4, columnWidth: "60%", distributed: false } },
                      colors: ["#10b981", "#6366f1"],
                      legend: { show: true, position: "top" },
                      tooltip: { y: { formatter: (v, { seriesIndex }) => seriesIndex === 0 ? fmt(v) + " L" : fmt(v) + " gal" } },
                    }}
                    series={[
                      { name: "CAD (L)", data: providers.map(p => round2(p.CAD?.qty)) },
                      { name: "USD (gal)", data: providers.map(p => round2(p.USD?.qty)) },
                    ]}
                    type="bar"
                    height={280}
                  />
                </div>

                <div className="db-card">
                  <p className="db-card-title">Cost by Provider — CAD ($) · USD ($)</p>
                  <ReactApexChart
                    options={{
                      ...barOptions,
                      plotOptions: { bar: { borderRadius: 4, columnWidth: "60%", distributed: false } },
                      colors: ["#10b981", "#6366f1"],
                      legend: { show: true, position: "top" },
                      tooltip: { y: { formatter: (v, { seriesIndex }) => (seriesIndex === 0 ? "CA$" : "US$") + fmt(v) } },
                    }}
                    series={[
                      { name: "CAD ($)", data: providers.map(p => round2(p.CAD?.amt)) },
                      { name: "USD ($)", data: providers.map(p => round2(p.USD?.amt)) },
                    ]}
                    type="bar"
                    height={280}
                  />
                </div>
              </div>

              {/* ── Volume & Cost by Product Code by Provider ── */}
              {(() => {
                // collect all unique product codes across all providers
                const allPrds = [...new Set(providers.flatMap(p => Object.keys(p.by_product || {})))].sort();
                const PRD_COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#f97316","#ec4899"];
                const providerLabels = providers.map(p => p.provider);

                // stacked series: one series per prd per currency
                const volSeries = allPrds.flatMap(prd => [
                  { name: `${prd} CAD(L)`, data: providers.map(p => round2(p.by_product?.[prd]?.CAD?.qty || 0)) },
                  { name: `${prd} USD(gal)`, data: providers.map(p => round2(p.by_product?.[prd]?.USD?.qty || 0)) },
                ]);
                const costSeries = allPrds.flatMap(prd => [
                  { name: `${prd} CA$`, data: providers.map(p => round2(p.by_product?.[prd]?.CAD?.amt || 0)) },
                  { name: `${prd} US$`, data: providers.map(p => round2(p.by_product?.[prd]?.USD?.amt || 0)) },
                ]);

                const stackedOpts = (yLabel, tooltipFmt) => ({
                  chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
                  plotOptions: { bar: { borderRadius: 4, columnWidth: "50%" } },
                  dataLabels: { enabled: false },
                  xaxis: { categories: providerLabels, labels: { style: { fontSize: "12px", fontWeight: 600 } }, axisBorder: { show: false }, axisTicks: { show: false } },
                  yaxis: { labels: { formatter: v => Number(v).toLocaleString(), style: { fontSize: "11px", colors: "#9ca3af" } }, title: { text: yLabel, style: { fontSize: "11px", color: "#9ca3af" } } },
                  colors: PRD_COLORS,
                  legend: { position: "top", fontSize: "12px", markers: { radius: 4 } },
                  grid: { borderColor: "#f3f4f6", strokeDashArray: 4 },
                  fill: { opacity: 1 },
                  tooltip: { y: { formatter: tooltipFmt } },
                });

                return (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 0 }}>
                    <div className="db-card">
                      <p className="db-card-title">Fuel Volume by Product · Provider (L)</p>
                      <ReactApexChart
                        options={stackedOpts("Litres (L)", v => fmt(v) + " L")}
                        series={volSeries}
                        type="bar"
                        height={300}
                      />
                    </div>
                    <div className="db-card">
                      <p className="db-card-title">Cost by Product · Provider ($)</p>
                      <ReactApexChart
                        options={stackedOpts("Cost ($)", v => "$" + fmt(v))}
                        series={costSeries}
                        type="bar"
                        height={300}
                      />
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default DashboardComponent;
